import React, { useEffect, useState } from "react";
import { Save, Trash2, Plus, Shield } from "lucide-react";
import api from "../../lib/api";

const computeAge = (dob) => {
    if (!dob) return null;
    const b = new Date(dob);
    if (isNaN(b.getTime())) return null;
    const now = new Date();
    let age = now.getFullYear() - b.getFullYear();
    const m = now.getMonth() - b.getMonth();
    if (m < 0 || (m === 0 && now.getDate() < b.getDate())) age--;
    return age >= 0 && age < 100 ? age : null;
};

export default function AdminRosters() {
    const [teams, setTeams] = useState([]);
    const [tid, setTid] = useState("");
    const [players, setPlayers] = useState([]);
    const [bulk, setBulk] = useState("");
    const [bulkReplace, setBulkReplace] = useState(false);
    const [bulkResult, setBulkResult] = useState(null);
    const [busy, setBusy] = useState(false);

    // Single-add form state
    const [showAddForm, setShowAddForm] = useState(false);
    const [newPlayer, setNewPlayer] = useState({ number: "", name: "", is_goalie: false, dob: "", position: "" });

    useEffect(() => {
        api.get("/teams").then(r => { setTeams(r.data); if (r.data[0]) setTid(r.data[0].id); });
    }, []);

    const loadPlayers = (id) => id && api.get(`/players?team_id=${id}`).then(r => setPlayers(r.data));
    useEffect(() => { loadPlayers(tid); setBulkResult(null); }, [tid]);

    const importBulk = async () => {
        if (!tid || !bulk.trim()) return;
        if (bulkReplace && !window.confirm("Vous allez REMPLACER l'alignement complet. Continuer ?")) return;
        setBusy(true);
        try {
            const { data } = await api.post("/players/bulk", { team_id: tid, text: bulk, replace: bulkReplace });
            setBulkResult(data);
            setBulk("");
            loadPlayers(tid);
        } finally { setBusy(false); }
    };

    const addSingle = async () => {
        if (!tid) return;
        const num = parseInt(newPlayer.number, 10);
        if (!num && num !== 0) { alert("Numéro invalide"); return; }
        if (!newPlayer.name.trim()) { alert("Nom requis"); return; }
        setBusy(true);
        try {
            await api.post("/players", {
                team_id: tid,
                number: num,
                name: newPlayer.name.trim(),
                is_goalie: !!newPlayer.is_goalie,
                dob: newPlayer.dob || "",
                position: newPlayer.is_goalie ? "gardien" : (newPlayer.position || ""),
            });
            setNewPlayer({ number: "", name: "", is_goalie: false, dob: "", position: "" });
            setShowAddForm(false);
            loadPlayers(tid);
        } finally { setBusy(false); }
    };

    const del = async (pid) => {
        if (!window.confirm("Supprimer ce joueur ?")) return;
        await api.delete(`/players/${pid}`);
        loadPlayers(tid);
    };

    const updatePlayer = async (p, patch = {}) => {
        const merged = { ...p, ...patch };
        await api.put(`/players/${p.id}`, {
            team_id: merged.team_id,
            number: parseInt(merged.number, 10) || 0,
            name: merged.name || "",
            position: merged.position || (merged.is_goalie ? "gardien" : ""),
            dob: merged.dob || "",
            is_goalie: !!merged.is_goalie,
        });
        loadPlayers(tid);
    };

    return (
        <div data-testid="admin-rosters">
            <h1 className="font-display text-4xl uppercase tracking-widest mb-6">Rosters</h1>
            <div className="glass-card p-5 mb-6">
                <label className="label-mini block mb-2">Équipe</label>
                <select className="input-field max-w-md" value={tid} onChange={e => setTid(e.target.value)} data-testid="roster-team-select">
                    {teams.map(t => <option key={t.id} value={t.id}>{t.name} — {t.category}</option>)}
                </select>
            </div>

            <div className="grid lg:grid-cols-2 gap-6">
                {/* LEFT: Bulk import + single-add */}
                <div className="space-y-6">
                    <div className="glass-card p-5">
                        <h3 className="font-display text-2xl uppercase tracking-widest mb-3 text-[#7FD93C]">Import en lot</h3>
                        <p className="text-xs text-white/60 mb-3">
                            Format : <code className="bg-black/40 px-1.5 py-0.5">numéro - nom</code> par ligne.
                            <br />Préfixe <code className="bg-black/40 px-1.5 py-0.5">G</code> ou suffixe <code className="bg-black/40 px-1.5 py-0.5">(G)</code> pour indiquer un gardien.
                        </p>
                        <textarea
                            className="input-field font-mono text-sm"
                            rows={8}
                            placeholder={"G 30 - Léa Goalie\n12 - Alice Dubois\n7 - Bob Roy (G)"}
                            value={bulk}
                            onChange={e => setBulk(e.target.value)}
                            data-testid="bulk-roster-text"
                        />
                        <label className="flex items-center gap-2 mt-3 text-sm cursor-pointer" data-testid="bulk-replace-label">
                            <input type="checkbox" checked={bulkReplace} onChange={e => setBulkReplace(e.target.checked)} data-testid="bulk-replace-checkbox" />
                            <span className="text-white/70">Remplacer l&apos;alignement existant (efface tout avant d&apos;importer)</span>
                        </label>
                        <button onClick={importBulk} disabled={busy || !bulk.trim()} className="btn-primary mt-3 disabled:opacity-50" data-testid="bulk-roster-submit">
                            <Save size={14} /> {bulkReplace ? "Remplacer" : "Ajouter à l'alignement"}
                        </button>
                        {bulkResult && (
                            <div className="mt-3 text-xs text-[#7FD93C]" data-testid="bulk-result">
                                {bulkResult.inserted} ajouté(s){bulkResult.updated > 0 && `, ${bulkResult.updated} mis à jour`}.
                            </div>
                        )}
                    </div>

                    <div className="glass-card p-5">
                        <div className="flex items-center justify-between mb-3">
                            <h3 className="font-display text-2xl uppercase tracking-widest text-[#4FE0D5]">Ajouter un joueur</h3>
                            {!showAddForm && (
                                <button onClick={() => setShowAddForm(true)} className="btn-ghost flex items-center gap-1" data-testid="btn-show-add-player">
                                    <Plus size={14} /> Nouveau
                                </button>
                            )}
                        </div>
                        {showAddForm && (
                            <div className="space-y-2" data-testid="add-player-form">
                                <div className="grid grid-cols-6 gap-2">
                                    <input type="number" min="0" max="99" placeholder="Nº" className="input-field col-span-1" value={newPlayer.number}
                                        onChange={e => setNewPlayer({ ...newPlayer, number: e.target.value })} data-testid="add-player-number" />
                                    <input placeholder="Nom complet" className="input-field col-span-5" value={newPlayer.name}
                                        onChange={e => setNewPlayer({ ...newPlayer, name: e.target.value })} data-testid="add-player-name" />
                                </div>
                                <div className="grid grid-cols-2 gap-2">
                                    <input type="date" className="input-field" value={newPlayer.dob}
                                        onChange={e => setNewPlayer({ ...newPlayer, dob: e.target.value })} data-testid="add-player-dob" title="Date de naissance" />
                                    <label className="flex items-center gap-2 px-3 border border-white/10 bg-black/20 cursor-pointer">
                                        <input type="checkbox" checked={newPlayer.is_goalie}
                                            onChange={e => setNewPlayer({ ...newPlayer, is_goalie: e.target.checked })} data-testid="add-player-goalie" />
                                        <span className="text-sm flex items-center gap-1"><Shield size={12} className="text-[#4FE0D5]" /> Gardien</span>
                                    </label>
                                </div>
                                <div className="flex gap-2 pt-1">
                                    <button onClick={addSingle} disabled={busy} className="btn-primary" data-testid="add-player-submit">
                                        <Plus size={14} /> Ajouter
                                    </button>
                                    <button onClick={() => { setShowAddForm(false); setNewPlayer({ number: "", name: "", is_goalie: false, dob: "", position: "" }); }} className="btn-ghost">
                                        Annuler
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* RIGHT: Roster list */}
                <div className="glass-card p-5">
                    <h3 className="font-display text-2xl uppercase tracking-widest mb-3 text-[#4FE0D5]">Alignement actuel ({players.length})</h3>
                    <div className="grid gap-2 max-h-[600px] overflow-y-auto pr-2">
                        {players.length === 0 && <div className="text-white/40 text-sm">Aucun joueur.</div>}
                        {players.map(p => {
                            const age = computeAge(p.dob);
                            return (
                                <div key={p.id} className={`flex flex-wrap items-center gap-2 py-2 px-2 border-l-4 ${p.is_goalie ? "border-[#4FE0D5] bg-[#4FE0D5]/5" : "border-transparent"}`} data-testid={`roster-row-${p.id}`}>
                                    <input type="number" className="input-field w-16 sm:w-20" defaultValue={p.number} onBlur={(e) => updatePlayer(p, { number: e.target.value })} title="Numéro" />
                                    <input className="input-field flex-1 min-w-[140px]" defaultValue={p.name} onBlur={(e) => updatePlayer(p, { name: e.target.value })} title="Nom" />
                                    <label className={`flex items-center gap-1 px-2 border cursor-pointer text-xs whitespace-nowrap ${p.is_goalie ? "border-[#4FE0D5] bg-[#4FE0D5]/10 text-[#4FE0D5]" : "border-white/10"}`} title="Gardien">
                                        <input type="checkbox" checked={!!p.is_goalie} onChange={(e) => updatePlayer(p, { is_goalie: e.target.checked })} data-testid={`roster-goalie-${p.id}`} />
                                        <Shield size={12} />
                                    </label>
                                    <div className="flex items-center gap-1">
                                        <input type="date" className="input-field w-[150px] text-xs" defaultValue={p.dob || ""} onBlur={(e) => updatePlayer(p, { dob: e.target.value })} title="Date de naissance" data-testid={`roster-dob-${p.id}`} />
                                        {age !== null && <span className="text-[10px] text-white/50 shrink-0" data-testid={`roster-age-${p.id}`}>{age} ans</span>}
                                    </div>
                                    <button onClick={() => del(p.id)} className="btn-ghost text-rose-400 border-rose-400/30" title="Supprimer" data-testid={`btn-del-player-${p.id}`}><Trash2 size={12} /></button>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
        </div>
    );
}
